import { useCallback, useRef, useState, useEffect, useMemo } from "react";
import type { ThinkingMode, AiMessage } from "@ms/shared-components/comps/CompsAi";
import { buildTreeData } from "../utils/treeDataUtils";
import { createThinkingModes } from "../configs/panelAnalysisConfig";
import { initializeAiService, callAiService } from "../service/aiService";
import { aiConfigApi, type ComponentConfig } from "../../../service/aiConfigApi";
import { convertStoreComponentClickToStandard, type StandardAiComponentMessage } from "../utils/aiMessageConverter";
import {
  StoreComponentType,
  DataSourceType,
  StatCardType,
  type StoreComponentClickParams,
  type ComponentDataPackage,
  type TimeDirection,
  type DataSource,
} from "../types/aiAssistantTypes";

export {
  StoreComponentType,
  DataSourceType,
  StatCardType,
  type StoreComponentClickParams,
  type ComponentDataPackage,
  type TimeDirection,
  type DataSource,
};

/**
 * AI 请求参数
 */
export interface AIRequestParams {
  dataSources: DataSource[]; // 数据源列表（1个或多个）
  thinkingMode: ThinkingMode; // 思考方式
  clearPrevious: boolean; // 是否清空之前的内容（单击=true，连续选择=false）
}

/**
 * 统计卡片点击参数
 */
export interface StatCardClickParams {
  statCardType: StatCardType;
  data: any; // 统计卡片对应的数据（如门店列表）
  metadata?: {
    label?: string;
    count?: number; // 数量（如3家、5家等）
    [key: string]: any;
  };
  clearPrevious?: boolean; // 默认为 true（单击）
}


/**
 * 门店标题点击参数
 */
export interface StoreTitleClickParams {
  storeId: string;
  storeName: string;
  storeData: any; // 门店完整数据
  metadata?: {
    label?: string;
    description?: string;
    [key: string]: any;
  };
  clearPrevious?: boolean; // 默认为 true（单击）
}

/**
 * 多选数据源参数
 */
export interface MultipleSelectionParams {
  dataSources: DataSource[]; // 多个数据源
  thinkingMode: ThinkingMode; // 思考方式
}

// ==================== useAiComponent 相关类型 ====================

/**
 * 统计卡片项类型
 */
export interface StatCardItem {
  key: string;
  title: string;
  value: number | string;
  [key: string]: any;
}

/**
 * 统计卡片点击事件参数
 */
export interface StatCardClickEvent {
  item: StatCardItem;
  event: React.MouseEvent;
}

/**
 * 门店数据项类型
 */
export interface StoreItem {
  id: string;
  name: string;
  description?: string;
  [key: string]: any;
}

/**
 * 组件标题点击参数
 */
export interface ComponentTitleClickParams {
  storeId: string | number;
  componentType: string; // 组件类型字符串（如 "room-status"）
  /** 由 buildComponentDataPackage 统一组织：current + last7Days/last30Days/last12Months */
  componentData: ComponentDataPackage;
}

/**
 * 按 title（componentType）做 case 的配置表
 * 含 componentId、展示名、枚举值、时间方向（past=过去 7天/30天/12月，future=未来）
 */
const COMPONENT_CONFIG: Record<
  string,
  {
    componentId: string;
    label: string;
    storeComponentType: StoreComponentType;
    /** 时间方向（仅旧四段接口组件）；内置多档包勿用 */
    timeDirection?: TimeDirection;
  }
> = {
  [StoreComponentType.STORE_OVERVIEW]: {
    componentId: "card:store-overview",
    label: "门店信息概览",
    storeComponentType: StoreComponentType.STORE_OVERVIEW,
    timeDirection: "past",
  },
  [StoreComponentType.ROOM_STATUS]: {
    componentId: "table:room-status",
    label: "实时房态",
    storeComponentType: StoreComponentType.ROOM_STATUS,
    timeDirection: "past",
  },
  [StoreComponentType.ORDER_TRAFFIC]: {
    componentId: "chart:order-traffic",
    label: "订单流速",
    storeComponentType: StoreComponentType.ORDER_TRAFFIC,
  },
  [StoreComponentType.ROOM_STATUS_RECENTLY]: {
    componentId: "table:room-status-recently",
    label: "远期房态",
    storeComponentType: StoreComponentType.ROOM_STATUS_RECENTLY,
    timeDirection: "past",
  },
  [StoreComponentType.PRICE_ALERT]: {
    componentId: "card:price-alert",
    label: "价格倒挂",
    storeComponentType: StoreComponentType.PRICE_ALERT,
    timeDirection: "past",
  },
  [StoreComponentType.REVPAR]: {
    componentId: "chart:revpar",
    label: "可售房收入",
    storeComponentType: StoreComponentType.REVPAR,
  },
  [StoreComponentType.CHANNEL_INFO]: {
    componentId: "chart:channel-info",
    label: "渠道收入",
    storeComponentType: StoreComponentType.CHANNEL_INFO,
  },
  [StoreComponentType.CHANNEL_PRICE]: {
    componentId: "chart:channel-price",
    label: "渠道房价",
    storeComponentType: StoreComponentType.CHANNEL_PRICE,
  },
  [StoreComponentType.ROOM_HEALTH]: {
    componentId: "chart:room-health",
    label: "房态健康",
    storeComponentType: StoreComponentType.ROOM_HEALTH,
  },
  [StoreComponentType.CUSTOMER_SOURCE]: {
    componentId: "chart:customer-source",
    label: "客源结构",
    storeComponentType: StoreComponentType.CUSTOMER_SOURCE,
  },
  [StoreComponentType.DISCOUNT_ANALYSIS]: {
    componentId: "chart:discount-analysis",
    label: "折扣率",
    storeComponentType: StoreComponentType.DISCOUNT_ANALYSIS,
  },
  [StoreComponentType.CHANNEL_ANALYSIS]: {
    componentId: "chart:channel-analysis",
    label: "渠道订单",
    storeComponentType: StoreComponentType.CHANNEL_ANALYSIS,
  },
  [StoreComponentType.OCCUPANCY_ANALYSIS]: {
    componentId: "chart:occupancy-analysis",
    label: "出租率",
    storeComponentType: StoreComponentType.OCCUPANCY_ANALYSIS,
  },
  [StoreComponentType.OPERATION_EFFICIENCY]: {
    componentId: "chart:operation-efficiency",
    label: "运营效率",
    storeComponentType: StoreComponentType.OPERATION_EFFICIENCY,
  },
};

/** 按组件类型取时间方向（仅仍走四段/旧接口的组件）；内置多档包勿调用 */
export function getComponentTimeDirection(componentType: string): TimeDirection {
  return COMPONENT_CONFIG[componentType]?.timeDirection ?? "past";
}

/** 按组件类型取中文展示名，供发给 CompsAi 的 metadata.label 使用 */
export function getComponentLabel(componentType: string): string {
  return COMPONENT_CONFIG[componentType]?.label ?? componentType;
}

/** 所有组件类型与中文名列表，供 AI 助手「组件列表 + 分析」使用 */
export const ALL_COMPONENT_LIST: { componentType: string; label: string }[] =
  Object.entries(COMPONENT_CONFIG).map(([componentType, c]) => ({
    componentType,
    label: c.label,
  }));

// ==================== useAiAssistant 核心 Hook ====================

/** useAiAssistant 可选配置 */
export interface UseAiAssistantOptions {
  /** 为 true 时不请求 /api/ai/config/page（本页思考方式来自 get-thinking-methods，可避免 404） */
  skipPageConfig?: boolean;
}

/**
 * 使用 AI 助手的 Hook
 */
export const useAiAssistant = (
  onDataUpdate?: (data: any, label: string) => void,
  onTreeDataUpdate?: (treeData: any[]) => void,
  options?: UseAiAssistantOptions
): {
  aiMessages: AiMessage[];
  loading: boolean;
  handleThinkingModeClick: (params: { treeData: any[]; thinkingMode: ThinkingMode }) => Promise<void>;
  clearMessages: () => void;
  clearAll: () => void; // 全面清理所有内容
  currentData: any;
  currentDataLabel: string;
  currentTreeData: any[];
  thinkingModes: ThinkingMode[];
  handleStatCardClick: (params: StatCardClickParams, thinkingMode?: ThinkingMode | null) => void;
  handleStoreTitleClick: (params: StoreTitleClickParams, thinkingMode?: ThinkingMode | null) => void;
  handleStoreComponentClick: (params: StoreComponentClickParams, thinkingMode?: ThinkingMode | null) => void;
  handleMultipleSelection: (params: MultipleSelectionParams) => void;
  buildStatCardDataSource: (params: StatCardClickParams) => DataSource;
  buildStoreTitleDataSource: (params: StoreTitleClickParams) => DataSource;
  buildStoreComponentDataSource: (params: StoreComponentClickParams) => DataSource;
  clearDataSources: () => void;
  getCurrentDataSources: () => DataSource[];
  handleStatCardClickFromComponent: (item: StatCardItem, event: React.MouseEvent, getCardData?: (cardKey: string) => any) => void;
  handleStoreTitleClickFromComponent: (storeItem: StoreItem) => void;
  handleComponentTitleClickFromComponent: (params: ComponentTitleClickParams) => void;
  getComponentConfig: (componentId: string) => ComponentConfig | undefined;
  getComponentThinkingModes: (componentId: string) => ThinkingMode[];
  getComponentDefaultThinkingMode: (componentId: string) => ThinkingMode | null;
} => {
  // 存储当前的数据源列表（用于连续选择）
  const currentDataSourcesRef = useRef<DataSource[]>([]);

  // 初始化时确保是空数组而不是 undefined
  if (!currentDataSourcesRef.current) {
    currentDataSourcesRef.current = [];
  }

  // AI 消息和加载状态（本地管理）
  const [aiMessages, setAiMessages] = useState<AiMessage[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const messageIdRef = useRef<number>(0);

  // 当前选中的数据对象（用于显示在 CompsAi 中）
  const [currentData, setCurrentData] = useState<any>(null);
  const [currentDataLabel, setCurrentDataLabel] = useState<string>("门店数据");

  // 当前树形数据（用于 CompsAi 显示）
  const [currentTreeData, setCurrentTreeData] = useState<any[]>([]);

  // 页面配置（从后端加载）
  const [pageConfig, setPageConfig] = useState<ComponentConfig[]>([]);

  // 初始化 AI 服务配置
  useEffect(() => {
    initializeAiService();
  }, []);

  // 加载页面配置（skipPageConfig 时跳过，本页思考方式来自 get-thinking-methods）
  useEffect(() => {
    if (options?.skipPageConfig) return;

    const loadPageConfig = async () => {
      try {
        const config = await aiConfigApi.getPageConfig("msCentral", "store-revenue-monitor");
        console.log("✅ [页面配置] 加载成功:", config);
        setPageConfig(config.components || []);
      } catch (error: any) {
        console.error("❌ [页面配置] 加载失败:", error);
        // 如果加载失败，使用默认配置
        setPageConfig([]);
      }
    };

    loadPageConfig();
  }, [options?.skipPageConfig]);

  // 思考方式配置（从后端配置中提取，如果没有则使用默认配置）
  // 注意：必须在 getComponentThinkingModes 和 getComponentDefaultThinkingMode 之前定义
  const thinkingModes = useMemo(() => {
    // 如果后端配置已加载，尝试从配置中提取思考方式
    // 否则使用默认配置
    if (pageConfig.length > 0) {
      // 从第一个组件中提取思考方式（可以根据需要调整逻辑）
      const firstComponent = pageConfig[0];
      if (firstComponent?.thinkingModes && firstComponent.thinkingModes.length > 0) {
        return firstComponent.thinkingModes.map((mode) => ({
          id: mode.thinkingModeId,
          label: mode.label,
          description: mode.description,
          icon: undefined, // 后端配置中没有 icon
        }));
      }
    }
    // 使用默认配置
    return createThinkingModes();
  }, [pageConfig]);

  // 根据 componentId 获取组件配置
  const getComponentConfig = useCallback((componentId: string): ComponentConfig | undefined => {
    return pageConfig.find(c => c.componentId === componentId);
  }, [pageConfig]);

  // 根据 componentId 获取组件的思考方式列表
  const getComponentThinkingModes = useCallback((componentId: string): ThinkingMode[] => {
    const component = getComponentConfig(componentId);
    if (!component || !component.thinkingModes || component.thinkingModes.length === 0) {
      return thinkingModes; // 如果没有配置，返回默认思考方式
    }

    return component.thinkingModes
      .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
      .map(mode => ({
        id: mode.thinkingModeId,
        label: mode.label || mode.thinkingModeId,
        description: mode.description || '',
      }));
  }, [getComponentConfig, thinkingModes]);

  // 根据 componentId 获取组件的默认思考方式
  const getComponentDefaultThinkingMode = useCallback((componentId: string): ThinkingMode | null => {
    const component = getComponentConfig(componentId);
    if (!component || !component.thinkingModes || component.thinkingModes.length === 0) {
      // 如果没有配置，返回第一个默认思考方式
      return thinkingModes.length > 0 ? thinkingModes[0] : null;
    }

    // 查找 isDefault 为 true 的思考方式
    const defaultMode = component.thinkingModes.find(mode => mode.isDefault);
    if (defaultMode) {
      return {
        id: defaultMode.thinkingModeId,
        label: defaultMode.label || defaultMode.thinkingModeId,
        description: defaultMode.description || '',
      };
    }

    // 如果没有默认的，返回第一个
    const firstMode = component.thinkingModes.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))[0];
    return {
      id: firstMode.thinkingModeId,
      label: firstMode.label || firstMode.thinkingModeId,
      description: firstMode.description || '',
    };
  }, [getComponentConfig, thinkingModes]);

  // 生成消息ID
  const generateMessageId = useCallback(() => {
    messageIdRef.current += 1;
    return `msg-${Date.now()}-${messageIdRef.current}`;
  }, []);

  // 处理思考方式点击（新的接口，使用树形结构）
  const handleThinkingModeClick = useCallback(
    async (params: { treeData: any[]; thinkingMode: ThinkingMode }) => {
      const { treeData, thinkingMode } = params;

      if (!thinkingMode) {
        console.warn("⚠️ [思考方式点击] thinkingMode 为空");
        return;
      }

      if (!treeData || treeData.length === 0) {
        console.warn("⚠️ [思考方式点击] treeData 为空");
        return;
      }

      console.log("🤔 [思考方式点击] 开始处理:", {
        thinkingMode: thinkingMode.id,
        treeDataLength: treeData.length,
        treeData,
      });

      setLoading(true);

      try {
        // 调用 AI 服务，传递树形结构数据
        const response = await callAiService({
          data: treeData, // 传递树形结构
          thinkingMode,
        });

        console.log("✅ [思考方式点击] AI 调用成功，响应长度:", response.length);

        // 创建新消息（新消息会显示在最上面）
        const newMessage: AiMessage = {
          id: generateMessageId(),
          content: response,
          timestamp: Date.now(),
          thinkingMode,
        };

        // 将新消息添加到数组开头（新消息在上）
        setAiMessages((prev) => [newMessage, ...prev]);
      } catch (error: any) {
        console.error("❌ [思考方式点击] AI 调用失败:", error);
        // 添加错误消息到列表
        const errorMessage: AiMessage = {
          id: generateMessageId(),
          content: `错误: ${error.message || "AI 调用失败"}`,
          timestamp: Date.now(),
          thinkingMode,
        };
        setAiMessages((prev) => [errorMessage, ...prev]);
      } finally {
        setLoading(false);
      }
    },
    [generateMessageId]
  );

  // 处理统计卡片点击后的数据更新回调
  const handleStatCardDataUpdate = useCallback(
    (data: any, label: string) => {
      console.log("📊 [数据更新] 更新当前数据对象:", { label, dataLength: Array.isArray(data) ? data.length : 1 });
      setCurrentData(data);
      setCurrentDataLabel(label);
      if (onDataUpdate) {
        onDataUpdate(data, label);
      }
    },
    [onDataUpdate]
  );

  // 处理树形数据更新回调
  const handleTreeDataUpdate = useCallback(
    (treeData: any[]) => {
      console.log("🌳 [树形数据更新] 更新当前树形数据:", { treeDataLength: treeData?.length || 0 });
      setCurrentTreeData(treeData || []);
      if (onTreeDataUpdate) {
        onTreeDataUpdate(treeData || []);
      }
    },
    [onTreeDataUpdate]
  );

  /**
   * 构建统计卡片数据源
   */
  const buildStatCardDataSource = useCallback(
    (params: StatCardClickParams): DataSource => {
      const { statCardType, data, metadata = {} } = params;

      // 统计卡片标签映射（所有统计卡片统一使用此映射）
      const statCardLabels: Record<StatCardType, string> = {
        [StatCardType.PRICE_ADJUSTMENT_STORES]: "待调价门店",
        [StatCardType.PRICE_ANOMALY]: "价格异常",
        [StatCardType.FULLY_BOOKED]: "满房",
        [StatCardType.CUSTOMER_SOURCE_ANOMALY]: "客源异常",
        [StatCardType.SALES_STOPPED_ANOMALY]: "停售异常",
        [StatCardType.REVIEW_ANOMALY]: "评价异常",
        [StatCardType.MAJOR_EVENT_AREAS]: "大事件地区",
        [StatCardType.OCCUPANCY_RATE_ANOMALY]: "出租率异常",
      };

      // 构建完整的数据源对象
      const dataSource: DataSource = {
        id: `stat-card:${statCardType}`,
        type: DataSourceType.STAT_CARD,
        statCardType,
        data,
        metadata: {
          label: metadata.label || statCardLabels[statCardType],
          count: metadata.count,
          timestamp: new Date().toISOString(),
          ...metadata,
        },
      };

      console.log("📋 [构建数据源] 统计卡片数据源:", {
        id: dataSource.id,
        type: dataSource.type,
        statCardType: dataSource.statCardType,
        label: dataSource.metadata.label,
        count: dataSource.metadata.count,
        dataLength: Array.isArray(dataSource.data) ? dataSource.data.length : 1,
      });

      return dataSource;
    },
    []
  );

  /**
   * 构建门店组件数据源
   */
  const buildStoreComponentDataSource = useCallback(
    (params: StoreComponentClickParams): DataSource => {
      const { storeId, componentType, data, metadata = {} } = params;

      // 与 COMPONENT_CONFIG 保持一致：按 componentType 做 case
      const componentLabels: Record<StoreComponentType, string> = {
        [StoreComponentType.ROOM_STATUS]: "实时房态",
        [StoreComponentType.ORDER_TRAFFIC]: "订单流速",
        [StoreComponentType.ROOM_STATUS_RECENTLY]: "远期房态",
        [StoreComponentType.PRICE_ALERT]: "价格倒挂",
        [StoreComponentType.REVPAR]: "可售房收入",
        [StoreComponentType.CHANNEL_INFO]: "渠道收入",
        [StoreComponentType.CHANNEL_PRICE]: "渠道房价",
        [StoreComponentType.ROOM_HEALTH]: "房态健康",
        [StoreComponentType.CUSTOMER_SOURCE]: "客源结构",
        [StoreComponentType.DISCOUNT_ANALYSIS]: "折扣率",
        [StoreComponentType.CHANNEL_ANALYSIS]: "渠道订单",
        [StoreComponentType.OCCUPANCY_ANALYSIS]: "出租率",
        [StoreComponentType.OPERATION_EFFICIENCY]: "运营效率",
      };

      const componentIdMap: Record<string, string> = {
        [StoreComponentType.ROOM_STATUS]: "table:room-status",
        [StoreComponentType.ORDER_TRAFFIC]: "chart:order-traffic",
        [StoreComponentType.ROOM_STATUS_RECENTLY]: "table:room-status-recently",
        [StoreComponentType.PRICE_ALERT]: "card:price-alert",
        [StoreComponentType.REVPAR]: "chart:revpar",
        [StoreComponentType.CHANNEL_INFO]: "chart:channel-info",
        [StoreComponentType.CHANNEL_PRICE]: "chart:channel-price",
        [StoreComponentType.ROOM_HEALTH]: "chart:room-health",
        [StoreComponentType.CUSTOMER_SOURCE]: "chart:customer-source",
        [StoreComponentType.DISCOUNT_ANALYSIS]: "chart:discount-analysis",
        [StoreComponentType.CHANNEL_ANALYSIS]: "chart:channel-analysis",
        [StoreComponentType.OCCUPANCY_ANALYSIS]: "chart:occupancy-analysis",
        [StoreComponentType.OPERATION_EFFICIENCY]: "chart:operation-efficiency",
      };

      // 尝试多种方式匹配 componentId
      let componentId = componentIdMap[componentType];
      if (!componentId) {
        componentId = componentIdMap[String(componentType)];
      }
      if (!componentId && typeof componentType === 'string') {
        componentId = componentIdMap[componentType];
      }

      console.log("🔍 [buildStoreComponentDataSource] componentId 映射:", {
        componentType,
        componentTypeType: typeof componentType,
        componentId,
        hasComponentId: !!componentId,
        availableKeys: Object.keys(componentIdMap),
      });

      // 确保 componentId 被正确设置（不会被 metadata 覆盖）
      const finalMetadata = {
        ...metadata,
        label: metadata.label || componentLabels[componentType],
        elementType: metadata.elementType,
        elementValue: metadata.elementValue,
        timestamp: new Date().toISOString(),
        componentId, // 添加 componentId 到 metadata，供 AI 服务使用（放在最后，确保不被覆盖）
      };

      console.log("📋 [buildStoreComponentDataSource] 构建数据源:", {
        id: `store:${storeId}:${componentType}`,
        componentType,
        componentId,
        hasComponentId: !!componentId,
        metadataComponentId: finalMetadata.componentId,
      });

      return {
        id: `store:${storeId}:${componentType}`,
        type: DataSourceType.STORE_COMPONENT,
        storeId,
        componentType,
        data,
        metadata: finalMetadata,
      };
    },
    []
  );

  /**
   * 处理统计卡片点击
   * @param params - 统计卡片点击参数
   * @param thinkingMode - 思考方式（可选，如果提供则立即调用AI，否则只更新数据）
   */
  const handleStatCardClick = useCallback(
    (
      params: StatCardClickParams,
      thinkingMode?: ThinkingMode | null
    ) => {
      const { clearPrevious = true } = params;

      console.log("🔧 [AI助手] 处理统计卡片点击:", {
        params,
        thinkingMode: thinkingMode?.id || "无（仅更新数据）",
        clearPrevious,
      });

      const dataSource = buildStatCardDataSource(params);
      console.log("📋 [AI助手] 构建的数据源:", dataSource);

      if (clearPrevious) {
        // 单击：清空之前的内容
        currentDataSourcesRef.current = [dataSource];
        console.log("🔄 [AI助手] 清空之前的内容，设置新数据源");
      } else {
        // 连续选择：追加到现有列表
        currentDataSourcesRef.current.push(dataSource);
        console.log("➕ [AI助手] 追加数据源到现有列表，当前数量:", currentDataSourcesRef.current.length);
      }

      // 更新数据对象（用于 CompsAi 显示）
      handleStatCardDataUpdate(dataSource.data, dataSource.metadata.label || "数据对象");

      // 更新树形数据（用于 CompsAi 显示）
      handleTreeDataUpdate(
        buildTreeData(
          currentDataSourcesRef.current,
          null, // 不传入思考方式，只更新数据
          clearPrevious,
          thinkingModes // 传入思考方式列表，用于为每个门店添加思考方式选项
        )
      );

      // 如果提供了思考方式，则立即调用AI
      if (thinkingMode) {
        // 确保 currentDataSourcesRef.current 是有效的数组
        const dataSources = currentDataSourcesRef.current || [];

        // 构建树形结构数据（传入思考方式列表以支持多层嵌套）
        const treeData = buildTreeData(
          dataSources,
          thinkingMode,
          clearPrevious,
          thinkingModes // 传入思考方式列表，用于为每个门店添加思考方式选项
        );

        console.log("📤 [AI助手] 立即调用AI:", {
          dataSourceCount: dataSources.length,
          clearPrevious,
          thinkingMode: thinkingMode.id,
          treeDataLength: treeData.length,
        });

        // 发送给 AI 组件（使用新的树形结构接口）
        try {
          handleThinkingModeClick({
            treeData,
            thinkingMode,
          });
          console.log("✅ [AI助手] 消息已成功发送给 CompsAi");
        } catch (error) {
          console.error("❌ [AI助手] 发送消息失败:", error);
        }
      } else {
        console.log("ℹ️ [AI助手] 仅更新数据，等待用户点击思考方式");
      }
    },
    [buildStatCardDataSource, handleThinkingModeClick, handleStatCardDataUpdate, handleTreeDataUpdate, thinkingModes]
  );

  /**
   * 构建门店标题数据源
   */
  const buildStoreTitleDataSource = useCallback(
    (params: StoreTitleClickParams): DataSource => {
      const { storeId, storeName, storeData, metadata = {} } = params;

      return {
        id: `store:${storeId}:title`,
        type: DataSourceType.STORE_COMPONENT,
        storeId,
        data: storeData,
        metadata: {
          label: metadata.label || storeName,
          description: metadata.description,
          timestamp: new Date().toISOString(),
          ...metadata,
        },
      };
    },
    []
  );

  /**
   * 处理门店标题点击
   * @param params - 门店标题点击参数
   * @param thinkingMode - 思考方式（可选，如果提供则立即调用AI，否则只更新数据）
   */
  const handleStoreTitleClick = useCallback(
    (
      params: StoreTitleClickParams,
      thinkingMode?: ThinkingMode | null
    ) => {
      const { clearPrevious = true } = params;

      console.log("🏪 [AI助手] 处理门店标题点击:", {
        params: {
          storeId: params.storeId,
          storeName: params.storeName,
        },
        thinkingMode: thinkingMode?.id || "无（仅更新数据）",
        clearPrevious,
      });

      const dataSource = buildStoreTitleDataSource(params);
      console.log("📋 [AI助手] 构建的门店标题数据源:", {
        id: dataSource.id,
        storeId: dataSource.storeId,
        label: dataSource.metadata.label,
      });

      if (clearPrevious) {
        // 单击：清空之前的内容
        currentDataSourcesRef.current = [dataSource];
        console.log("🔄 [AI助手] 清空之前的内容，设置新数据源");
      } else {
        // 连续选择：追加到现有列表
        currentDataSourcesRef.current.push(dataSource);
        console.log("➕ [AI助手] 追加数据源到现有列表，当前数量:", currentDataSourcesRef.current.length);
      }

      // 更新数据对象（用于 CompsAi 显示）
      handleStatCardDataUpdate(dataSource.data, dataSource.metadata.label || "门店数据");

      // 更新树形数据（用于 CompsAi 显示）
      handleTreeDataUpdate(
        buildTreeData(
          currentDataSourcesRef.current,
          null, // 不传入思考方式，只更新数据
          clearPrevious,
          thinkingModes // 传入思考方式列表，用于为每个门店添加思考方式选项
        )
      );

      // 如果提供了思考方式，则立即调用AI
      if (thinkingMode) {
        // 确保 currentDataSourcesRef.current 是有效的数组
        const dataSources = currentDataSourcesRef.current || [];

        // 构建树形结构数据（传入思考方式列表以支持多层嵌套）
        const treeData = buildTreeData(
          dataSources,
          thinkingMode,
          clearPrevious,
          thinkingModes // 传入思考方式列表，用于为每个门店添加思考方式选项
        );

        console.log("📤 [AI助手] 立即调用AI:", {
          dataSourceCount: dataSources.length,
          clearPrevious,
          thinkingMode: thinkingMode.id,
          treeDataLength: treeData.length,
        });

        // 发送给 AI 组件（使用新的树形结构接口）
        try {
          handleThinkingModeClick({
            treeData,
            thinkingMode,
          });
          console.log("✅ [AI助手] 消息已成功发送给 CompsAi");
        } catch (error) {
          console.error("❌ [AI助手] 发送消息失败:", error);
        }
      } else {
        console.log("ℹ️ [AI助手] 仅更新数据，等待用户点击思考方式");
      }
    },
    [buildStoreTitleDataSource, handleThinkingModeClick, handleStatCardDataUpdate, handleTreeDataUpdate, thinkingModes]
  );

  /**
   * 处理门店组件点击
   * @param params - 门店组件点击参数
   * @param thinkingMode - 思考方式（可选，如果提供则立即调用AI，否则只更新数据）
   */
  const handleStoreComponentClick = useCallback(
    (
      params: StoreComponentClickParams,
      thinkingMode?: ThinkingMode | null
    ) => {
      const { clearPrevious = true } = params;

      console.log("🔧 [AI助手] 处理门店组件点击:", {
        params: {
          storeId: params.storeId,
          componentType: params.componentType,
        },
        thinkingMode: thinkingMode?.id || "无（仅更新数据）",
        clearPrevious,
      });

      const dataSource = buildStoreComponentDataSource(params);
      console.log("📋 [AI助手] 构建的门店组件数据源:", {
        id: dataSource.id,
        storeId: dataSource.storeId,
        componentType: dataSource.componentType,
        label: dataSource.metadata.label,
        metadataComponentId: dataSource.metadata.componentId,
        hasComponentId: !!dataSource.metadata.componentId,
      });

      if (clearPrevious) {
        // 单击：清空之前的内容
        currentDataSourcesRef.current = [dataSource];
        console.log("🔄 [AI助手] 清空之前的内容，设置新数据源");
      } else {
        // 连续选择：追加到现有列表
        currentDataSourcesRef.current.push(dataSource);
        console.log("➕ [AI助手] 追加数据源到现有列表，当前数量:", currentDataSourcesRef.current.length);
      }

      // 更新数据对象（用于 CompsAi 显示）
      handleStatCardDataUpdate(dataSource.data, dataSource.metadata.label || "门店组件数据");

      // 更新树形数据（用于 CompsAi 显示）
      handleTreeDataUpdate(
        buildTreeData(
          currentDataSourcesRef.current,
          null, // 不传入思考方式，只更新数据
          clearPrevious,
          thinkingModes // 传入思考方式列表，用于为每个门店添加思考方式选项
        )
      );

      // 如果提供了思考方式，则立即调用AI
      if (thinkingMode) {
        // 确保 currentDataSourcesRef.current 是有效的数组
        const dataSources = currentDataSourcesRef.current || [];

        // ========== 转换为标准格式 ==========
        // 将 StoreComponentClickParams 转换为 StandardAiComponentMessage
        const standardMessage: StandardAiComponentMessage = convertStoreComponentClickToStandard(
          params,
          'store-revenue-monitor'
        );

        console.log("📋 [AI助手] 标准格式消息:", {
          msgId: standardMessage.msgId,
          source: standardMessage.source,
          eventData: {
            eventType: standardMessage.eventData.eventType,
            triggerTime: standardMessage.eventData.triggerTime,
            payloadKeys: Object.keys(standardMessage.eventData.payload),
          },
          hasContext: !!standardMessage.context,
        });

        // 验证数据源中的 componentId
        console.log("🔍 [AI助手] 验证数据源中的 componentId:", {
          dataSourceCount: dataSources.length,
          standardComponentId: standardMessage.source.componentId,
          dataSources: dataSources.map(ds => ({
            id: ds.id,
            componentType: ds.componentType,
            metadataComponentId: ds.metadata?.componentId,
            hasComponentId: !!ds.metadata?.componentId,
          })),
        });

        // 构建树形结构数据（传入思考方式列表以支持多层嵌套）
        const treeData = buildTreeData(
          dataSources,
          thinkingMode,
          clearPrevious,
          thinkingModes // 传入思考方式列表，用于为每个门店添加思考方式选项
        );

        // 验证树形数据中的 componentId
        console.log("🔍 [AI助手] 验证树形数据中的 componentId:", {
          treeDataLength: treeData.length,
          standardComponentId: standardMessage.source.componentId,
          firstNode: treeData[0] ? {
            id: treeData[0].id,
            hasDataSource: !!treeData[0].dataSource,
            dataSourceId: treeData[0].dataSource?.id,
            metadataComponentId: treeData[0].dataSource?.metadata?.componentId,
          } : null,
        });

        console.log("📤 [AI助手] 立即调用AI:", {
          dataSourceCount: dataSources.length,
          clearPrevious,
          thinkingMode: thinkingMode.id,
          treeDataLength: treeData.length,
          standardMessage: {
            msgId: standardMessage.msgId,
            componentId: standardMessage.source.componentId,
            pageId: standardMessage.source.pageId,
          },
        });

        // 发送给 AI 组件（使用新的树形结构接口）
        // 注意：标准格式消息已生成并记录，后续可以传递给新的 AI 组件
        try {
          handleThinkingModeClick({
            treeData,
            thinkingMode,
          });
          console.log("✅ [AI助手] 消息已成功发送给 CompsAi");
          console.log("📋 [AI助手] 标准格式消息已生成（可用于新 AI 组件）:", standardMessage);
        } catch (error) {
          console.error("❌ [AI助手] 发送消息失败:", error);
        }
      } else {
        // 即使不立即调用AI，也生成标准格式消息（用于后续使用）
        const standardMessage: StandardAiComponentMessage = convertStoreComponentClickToStandard(
          params,
          'store-revenue-monitor'
        );
        console.log("ℹ️ [AI助手] 仅更新数据，等待用户点击思考方式");
        console.log("📋 [AI助手] 标准格式消息已生成（待使用）:", {
          msgId: standardMessage.msgId,
          componentId: standardMessage.source.componentId,
          pageId: standardMessage.source.pageId,
        });
      }
    },
    [buildStoreComponentDataSource, handleThinkingModeClick, handleStatCardDataUpdate, handleTreeDataUpdate, thinkingModes]
  );

  /**
   * 处理多选数据源（用于跨组件组合分析）
   */
  const handleMultipleSelection = useCallback(
    (params: MultipleSelectionParams) => {
      const { dataSources: newDataSources, thinkingMode } = params;

      // 多选不清空，追加所有数据源
      currentDataSourcesRef.current = [
        ...(currentDataSourcesRef.current || []),
        ...(newDataSources || []),
      ];

      // 确保 currentDataSourcesRef.current 是有效的数组
      const allDataSources = currentDataSourcesRef.current || [];

      // 构建树形结构数据（传入思考方式列表以支持多层嵌套）
      const treeData = buildTreeData(
        allDataSources,
        thinkingMode,
        false, // 多选不清空
        thinkingModes // 传入思考方式列表，用于为每个门店添加思考方式选项
      );

      // 发送给 AI 组件（使用新的树形结构接口）
      handleThinkingModeClick({
        treeData,
        thinkingMode,
      });
    },
    [handleThinkingModeClick, thinkingModes]
  );

  /**
   * 清空数据源列表
   */
  const clearDataSources = useCallback(() => {
    currentDataSourcesRef.current = [];
  }, []);

  /**
   * 获取当前数据源列表
   */
  const getCurrentDataSources = useCallback((): DataSource[] => {
    return [...currentDataSourcesRef.current];
  }, []);

  // 清空消息
  const clearMessages = useCallback(() => {
    setAiMessages([]);
  }, []);

  /**
   * 全面清理所有内容
   * 包括：消息列表、树形数据、当前数据、数据源列表、加载状态等
   */
  const clearAll = useCallback(() => {
    console.log("🧹 [AI助手] 开始全面清理所有内容");

    // 1. 清理消息列表
    setAiMessages([]);
    console.log("✅ [AI助手] 已清理消息列表");

    // 2. 清理加载状态
    setLoading(false);
    console.log("✅ [AI助手] 已清理加载状态");

    // 3. 清理树形数据
    setCurrentTreeData([]);
    console.log("✅ [AI助手] 已清理树形数据");

    // 4. 清理当前数据
    setCurrentData(null);
    setCurrentDataLabel("门店数据");
    console.log("✅ [AI助手] 已清理当前数据");

    // 5. 清理数据源列表
    currentDataSourcesRef.current = [];
    console.log("✅ [AI助手] 已清理数据源列表");

    // 6. 清理 localStorage 中的相关缓存（可选）
    try {
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (
          key.startsWith('compsAi_') ||
          key.startsWith('aiCache_') ||
          key.startsWith('aiResult_') ||
          key.startsWith('store-revenue-monitor_')
        )) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
      if (keysToRemove.length > 0) {
        console.log(`✅ [AI助手] 已清理 ${keysToRemove.length} 个 localStorage 缓存项`);
      }
    } catch (error) {
      console.warn('⚠️ [AI助手] 清理 localStorage 缓存失败:', error);
    }

    console.log("🎉 [AI助手] 全面清理完成");
  }, []);

  // ==================== useAiComponent 相关功能 ====================

  /**
   * 处理统计卡片点击（从 useAiComponent 合并）
   */
  const handleStatCardClickFromComponent = useCallback(
    (item: StatCardItem, event: React.MouseEvent, getCardData?: (cardKey: string) => any) => {
      const { key, title, value } = item;

      console.log("📊 [统计卡片点击] 开始处理:", { key, title, value });

      // 获取统计卡片类型
      const statCardTypeMap: Record<string, StatCardType> = {
        "price-adjustment-stores": StatCardType.PRICE_ADJUSTMENT_STORES,
        "price-anomaly": StatCardType.PRICE_ANOMALY,
        "fully-booked": StatCardType.FULLY_BOOKED,
        "customer-source-anomaly": StatCardType.CUSTOMER_SOURCE_ANOMALY,
        "sales-stopped-anomaly": StatCardType.SALES_STOPPED_ANOMALY,
        "review-anomaly": StatCardType.REVIEW_ANOMALY,
        "major-event-areas": StatCardType.MAJOR_EVENT_AREAS,
        "occupancy-rate-anomaly": StatCardType.OCCUPANCY_RATE_ANOMALY,
      };
      const statCardType = statCardTypeMap[key];
      if (!statCardType) {
        console.warn(`⚠️ [统计卡片点击] 未知的统计卡片类型: ${key}`);
        return;
      }

      console.log("✅ [统计卡片点击] 卡片类型映射成功:", statCardType);

      // 获取实际数据（如果提供了 getCardData 函数）
      let cardData: any;
      if (getCardData) {
        cardData = getCardData(key);
        console.log("📦 [统计卡片点击] 使用自定义数据获取函数");
      } else {
        // 默认：所有统计卡片统一使用 value 作为数量，构建门店列表数组
        const count = typeof value === "number" ? value : Number.parseInt(String(value), 10) || 0;
        cardData = Array.from({ length: count }, (_, index) => ({
          id: `store-${key}-${index + 1}`,
          name: `门店${index + 1}`,
        }));
        console.log("📦 [统计卡片点击] 构建模拟数据，门店数量:", count);
      }

      console.log("📤 [统计卡片点击] 准备更新数据对象:", {
        statCardType,
        dataCount: Array.isArray(cardData) ? cardData.length : 1,
        label: title,
      });

      // 只更新数据对象，不立即调用AI
      try {
        handleStatCardClick(
          {
            statCardType,
            data: cardData,
            metadata: {
              label: title,
              count: typeof value === "number" ? value : Number.parseInt(String(value), 10) || 0,
            },
            clearPrevious: true, // 单击模式，清空之前的内容
          },
          // 不传入思考方式，只更新数据，不调用AI
          null as any
        );
        console.log("✅ [统计卡片点击] 数据对象已更新，等待用户点击思考方式");
      } catch (error) {
        console.error("❌ [统计卡片点击] 更新数据失败:", error);
      }
    },
    [handleStatCardClick]
  );

  /**
   * 处理门店标题点击（从 useAiComponent 合并）
   */
  const handleStoreTitleClickFromComponent = useCallback(
    (storeItem: StoreItem) => {
      const { id, name, description } = storeItem;

      console.log("🏪 [门店标题点击] 开始处理:", { id, name });

      // 只更新数据对象，不立即调用AI
      try {
        handleStoreTitleClick(
          {
            storeId: id,
            storeName: name,
            storeData: storeItem, // 传递完整的门店数据
            metadata: {
              label: name,
              description: description,
            },
            clearPrevious: true, // 单击模式，清空之前的内容
          },
          // 不传入思考方式，只更新数据，不调用AI
          null as any
        );
        console.log("✅ [门店标题点击] 数据对象已更新，等待用户点击思考方式");
      } catch (error) {
        console.error("❌ [门店标题点击] 更新数据失败:", error);
      }
    },
    [handleStoreTitleClick]
  );

  /**
   * 处理组件标题点击：所有组件标题都走这里，按 componentType（title）做 case
   * 各组件点击时先经 buildComponentDataPackage 打包（current + 7天/30天/12月），再传入
   */
  const handleComponentTitleClickFromComponent = useCallback(
    (params: ComponentTitleClickParams) => {
      const { storeId, componentType, componentData } = params;

      console.log("📊 [组件标题点击] 开始处理:", { storeId, componentType });
      // 浏览器控制台搜「四段时间」可确认是否从后端收到 current / last7Days / last30Days / last12Months
      const pkg = componentData as ComponentDataPackage;
      console.log("📦 [四段时间] 本门店本组件收到的数据包:", {
        storeId,
        componentType,
        timeDirection: pkg?.timeDirection,
        hasCurrent: !!pkg?.current,
        currentLen: Array.isArray(pkg?.current?.dataSource) ? pkg.current.dataSource.length : (pkg?.current ? "object" : 0),
        hasLast7Days: !!pkg?.last7Days,
        last7DaysLen: Array.isArray(pkg?.last7Days?.dataSource) ? pkg.last7Days.dataSource.length : (pkg?.last7Days ? "object" : 0),
        hasLast30Days: !!pkg?.last30Days,
        last30DaysLen: Array.isArray(pkg?.last30Days?.dataSource) ? pkg.last30Days.dataSource.length : (pkg?.last30Days ? "object" : 0),
        hasLast12Months: !!pkg?.last12Months,
        last12MonthsLen: Array.isArray(pkg?.last12Months?.dataSource) ? pkg.last12Months.dataSource.length : (pkg?.last12Months ? "object" : 0),
      });
      // 完整数据包，控制台展开可核对 current / last7Days / last30Days / last12Months 内容
      console.log("📦 [四段时间] 完整数据包（可展开查看）:", pkg);

      const config = COMPONENT_CONFIG[componentType];
      if (!config) {
        console.warn(`⚠️ [组件标题点击] 未知的组件类型: ${componentType}，请在 COMPONENT_CONFIG 中登记`);
        return;
      }

      try {
        handleStoreComponentClick(
          {
            storeId: String(storeId),
            componentType: config.storeComponentType,
            data: componentData,
            metadata: { label: config.label },
            clearPrevious: true,
          },
          null
        );
        console.log("✅ [组件标题点击] 数据对象已更新，请在 AI 面板中选择思考方式");
      } catch (error) {
        console.error("❌ [组件标题点击] 更新数据失败:", error);
      }
    },
    [handleStoreComponentClick]
  );

  return {
    // AI 消息和状态
    aiMessages,
    loading,
    handleThinkingModeClick,
    clearMessages,
    clearAll, // 全面清理所有内容
    // 当前数据对象（用于 CompsAi 显示）
    currentData,
    currentDataLabel,
    // 当前树形数据（用于 CompsAi 显示）
    currentTreeData,
    // 思考方式配置
    thinkingModes,
    // 处理方法（底层）
    handleStatCardClick,
    handleStoreTitleClick,
    handleStoreComponentClick,
    handleMultipleSelection,
    // 工具方法
    buildStatCardDataSource,
    buildStoreTitleDataSource,
    buildStoreComponentDataSource,
    clearDataSources,
    getCurrentDataSources,
    // 组件点击处理方法（高层，从 useAiComponent 合并）
    handleStatCardClickFromComponent,
    handleStoreTitleClickFromComponent,
    handleComponentTitleClickFromComponent,
    // 组件配置相关方法
    getComponentConfig,
    getComponentThinkingModes,
    getComponentDefaultThinkingMode,
  };
};
